// ─── Status Page recipient helpers (phone / email / SMS cost) ────────────────
// ONE definition, shared by the API (subscribe + send seams, allowlist storage)
// and the web app (the Settings allowlist editor + the SMS cost preview) — so a
// value the editor accepts is byte-for-byte the value the server stores, and a
// value it rejects can never reach GatewayAPI. Pure and dependency-free: this
// package compiles with `lib: ES2022` ONLY, so no DOM and no Node globals.
/** Separators humans type inside a phone number; all are noise, all get stripped.
 *  Includes NBSP (pasted from Word/Outlook) and the en/em dashes (pasted from a
 *  CRM), which look like a hyphen but are not one. */
const PHONE_SEPARATORS_RE = /[\s –—().\/-]/g;
/**
 * Normalize a human-typed phone number to E.164. Returns null for anything
 * unusable — it NEVER throws, so it is safe to run on raw user input, on a
 * pasted allowlist, and on every row of an import.
 *
 * A bare 8-digit number is treated as a Danish national number and gets
 * `defaultCountryCode` prefixed; anything else is assumed to already carry its
 * country code (with `+` or the `00` international prefix).
 */
export function normalizeMsisdn(raw, defaultCountryCode = '45', opts = {}) {
    const trimmed = String(raw ?? '').trim();
    // 32 is far beyond any real number — a longer value is a paste accident, not
    // a phone number, and we refuse it before doing any work on it.
    if (!trimmed || trimmed.length > 32)
        return null;
    let s = trimmed.replace(PHONE_SEPARATORS_RE, '');
    // "00" is the international access prefix — the same thing as a leading "+".
    if (s.startsWith('00'))
        s = `+${s.slice(2)}`;
    if (!/^[0-9+]+$/.test(s))
        return null;
    // A "+" is only ever legal as the very first character.
    if (s.indexOf('+', 1) !== -1)
        return null;
    // The default country code is config, not user input, but sanitise it anyway
    // so a stored "+45" can't smuggle a second "+" into the digits.
    const cc = String(defaultCountryCode ?? '').replace(/[^0-9]/g, '');
    let digits;
    if (s.startsWith('+'))
        digits = s.slice(1);
    else if (s.length === 8)
        digits = cc + s; // Danish national form
    else
        digits = s; // already international (no "+", but a country code)
    // E.164 allows at most 15 digits; below 8 it cannot be a mobile number.
    if (digits.length < 8 || digits.length > 15)
        return null;
    // A leading 0 here is a national trunk prefix that survived normalisation
    // (e.g. "0045 0 12…" or a foreign "07…"), never part of an E.164 number.
    if (digits.startsWith('0'))
        return null;
    // A Danish subscriber number never begins with 0 — that digit is the national
    // trunk prefix, so "+45 0…" is always a leftover from a badly pasted number.
    // Catching it here means a typo'd DK number fails in the allowlist editor
    // instead of at send time. (1 IS a legal leading digit — 1xxxxxxx numbers are
    // in service — so only 0 is rejected.)
    const strictDk = opts.strictDk ?? true;
    if (strictDk && digits.length === 10 && digits.startsWith('45') && digits[2] === '0')
        return null;
    // 15 nines (999_999_999_999_999) is well below Number.MAX_SAFE_INTEGER
    // (9_007_199_254_740_991), so the string→integer conversion is exact — and
    // GatewayAPI wants an integer MSISDN, not a string.
    const n = Number(digits);
    if (!Number.isSafeInteger(n) || n <= 1000 || n > 999_999_999_999_999)
        return null;
    return { msisdn: n, e164: `+${digits}` };
}
/**
 * Validate a GatewayAPI SMS sender ("from"). Numeric senders may be up to 15
 * digits (an MSISDN); an alphanumeric sender is capped at 11 characters and may
 * contain letters and digits only.
 *
 * GatewayAPI's OpenAPI schema says maxLength 18, but the semantic rule the
 * operators enforce is stricter — apply the strict rule so a bad sender fails at
 * CONFIG time (in Settings, with an explanation) instead of at send time, in the
 * middle of an incident, when nobody is watching.
 */
export function validateGatewayApiSender(sender) {
    const s = String(sender ?? '');
    if (s.length < 3)
        return { ok: false, error: 'Sender must be at least 3 characters.' };
    if (/\s/.test(s))
        return { ok: false, error: 'Sender cannot contain spaces.' };
    if (/^[0-9]+$/.test(s)) {
        return s.length > 15
            ? { ok: false, error: 'A numeric sender can be at most 15 digits.' }
            : { ok: true };
    }
    if (!/^[A-Za-z0-9]+$/.test(s)) {
        return { ok: false, error: 'Sender can only contain letters and digits (A–Z, 0–9) — no spaces, punctuation or symbols.' };
    }
    if (s.length > 11)
        return { ok: false, error: 'An alphanumeric sender can be at most 11 characters.' };
    return { ok: true };
}
// ─── GSM-7 (GSM 03.38) alphabet ──────────────────────────────────────────────
// The default SMS alphabet, laid out row by row as in the spec table. The Danish
// letters æøåÆØÅ ARE in the basic set (0x0B–0x0F, 0x1C–0x1D), so a Danish status
// message stays on the cheap 160-char encoding. 0x1B (ESC) is deliberately
// missing from row 1: it is the escape byte, not a character.
const GSM7_BASIC = new Set([
    ...'@£$¥èéùìòÇ\nØø\rÅå',
    ...'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ',
    ...' !"#¤%&\'()*+,-./',
    ...'0123456789:;<=>?',
    ...'¡ABCDEFGHIJKLMNO',
    ...'PQRSTUVWXYZÄÖÑÜ§',
    ...'¿abcdefghijklmno',
    ...'pqrstuvwxyzäöñüà',
]);
/** The extension table: each of these is sent as ESC + char, so it costs 2. */
const GSM7_EXTENDED = new Set([...'^{}\\[~]|€', '\f']);
/**
 * Estimate the encoding, billable character count and segment count of an SMS.
 * This drives the operator's cost preview before a status update goes out, so it
 * MUST be run on the FINAL text — including the appended stop link, which alone
 * is enough to push a message into a second segment.
 *
 * Note the estimate ignores one nicety of the real encoder: an ESC + char pair is
 * never split across a segment boundary, so a message packed with extension
 * characters can in theory need one more segment than this reports.
 */
export function estimateSmsSegments(text) {
    const s = String(text ?? '');
    let gsm7Chars = 0;
    let gsm7 = true;
    for (const ch of s) {
        if (GSM7_BASIC.has(ch))
            gsm7Chars += 1;
        else if (GSM7_EXTENDED.has(ch))
            gsm7Chars += 2;
        else {
            gsm7 = false;
            break;
        }
    }
    // Single GSM-7 SMS = 160 chars; concatenated ones lose 7 to the UDH → 153.
    if (gsm7) {
        return { encoding: 'gsm7', chars: gsm7Chars, segments: gsm7Chars <= 160 ? 1 : Math.ceil(gsm7Chars / 153) };
    }
    // UCS-2 bills per 16-bit unit, so an emoji (a surrogate PAIR) occupies 2 units
    // even though it is one character to a human — report code points as `chars`
    // (what the editor's counter shows) but do the segment maths on `s.length`.
    const units = s.length;
    return {
        encoding: 'ucs2',
        chars: [...s].length,
        segments: units <= 70 ? 1 : Math.ceil(units / 67),
    };
}
// ─── Email ───────────────────────────────────────────────────────────────────
/** Deliberately permissive, RFC-lite: real addresses are absurdly varied, and
 *  the authoritative validation is the delivery attempt itself. This only rejects
 *  what can never be an address (no @, whitespace, no dot in the domain). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Normalize an email for storage/comparison: trimmed and lowercased. Returns null
 * when it can't be an address, so the caller stores either a canonical value or
 * nothing. 320 = the RFC 5321 maximum (64 local + @ + 255 domain).
 */
export function normalizeEmail(raw) {
    const s = String(raw ?? '').trim().toLowerCase();
    if (!s || s.length > 320)
        return null;
    return EMAIL_RE.test(s) ? s : null;
}
/** What a value is masked to when it is too malformed/short to mask partially. */
const FULLY_MASKED = '•••';
/**
 * Mask an address for an operator-facing subscriber list: `martin@cb.dk` →
 * `m••••n@cb.dk`. Keeps the first and last character of the local part; a 1–2
 * character local part is masked completely (there is nothing left to hide
 * behind). Never throws — a malformed value returns a fully masked placeholder.
 */
export function maskEmail(addr) {
    const s = String(addr ?? '');
    const at = s.lastIndexOf('@');
    if (at <= 0 || at === s.length - 1)
        return FULLY_MASKED;
    const local = s.slice(0, at);
    const domain = s.slice(at + 1);
    const masked = local.length <= 2 ? '•'.repeat(local.length) : `${local[0]}${'•'.repeat(local.length - 2)}${local[local.length - 1]}`;
    return `${masked}@${domain}`;
}
// ITU-T E.164 gives zones 1 (NANP) and 7 (RU/KZ) a single-digit country code;
// every other code is 2 or 3 digits, and the 2-digit ones are a fixed list.
// Only used for masking, so an unlisted code simply falls back to 3 digits.
const TWO_DIGIT_COUNTRY_CODES = new Set(('20 27 30 31 32 33 34 36 39 40 41 43 44 45 46 47 48 49 51 52 53 54 55 56 57 58 ' +
    '60 61 62 63 64 65 66 81 82 84 86 90 91 92 93 94 95 98').split(' '));
/** Length of the country code at the head of an E.164 digit string (1–3). */
function countryCodeLength(digits) {
    if (digits[0] === '1' || digits[0] === '7')
        return 1;
    return TWO_DIGIT_COUNTRY_CODES.has(digits.slice(0, 2)) ? 2 : 3;
}
/** Group digits/dots in readable pairs: "1234" → "12 34". */
function inPairs(s) {
    return (s.match(/.{1,2}/g) ?? []).join(' ');
}
/**
 * Mask a number for an operator-facing subscriber list: `+4512345678` →
 * `+45 •• •• 56 78`. Shows the country code (so it is obvious which market a
 * subscriber is in) and the last 4 digits (enough for a human to recognise their
 * own number), masking everything between. Never throws.
 */
export function maskMsisdn(e164) {
    const digits = String(e164 ?? '').replace(/[^0-9]/g, '');
    // Defensive only — a normalized MSISDN is always ≥ 8 digits.
    if (digits.length < 6)
        return FULLY_MASKED;
    const cc = digits.slice(0, countryCodeLength(digits));
    const rest = digits.slice(cc.length);
    const tail = rest.slice(-4);
    const middle = rest.slice(0, -4);
    // Empty parts are dropped so a short number doesn't render a double space.
    return `+${cc} ${[inPairs('•'.repeat(middle.length)), inPairs(tail)].filter(Boolean).join(' ')}`.trim();
}
// ─── Allowlists ──────────────────────────────────────────────────────────────
// The allowlist gates who may subscribe to status notifications while the page
// is internal-only. Both matchers normalise BOTH sides, so however an entry was
// typed in Settings it still matches however a subscriber typed their address.
/**
 * Does an email pass the allowlist? An entry starting with `@` matches a whole
 * domain case-insensitively (`@cb.dk` allows anyone at cb.dk, but NOT a
 * subdomain like mail.cb.dk — list that separately); any other entry is an exact
 * address match after normalisation. An empty allowlist matches nothing.
 */
export function emailAllowlistMatches(normalizedEmail, allowlist) {
    const email = String(normalizedEmail ?? '').trim().toLowerCase();
    if (!email)
        return false;
    const domain = email.slice(email.lastIndexOf('@') + 1);
    for (const raw of allowlist) {
        const entry = String(raw ?? '').trim().toLowerCase();
        if (!entry)
            continue;
        if (entry.startsWith('@')) {
            if (domain && domain === entry.slice(1))
                return true;
        }
        else if (normalizeEmail(entry) === email)
            return true;
    }
    return false;
}
/**
 * Does a phone number pass the allowlist? Every entry is run through
 * {@link normalizeMsisdn} before comparing, so an entry typed as "20 12 34 56"
 * matches an input of "004520123456". An empty allowlist matches nothing.
 */
export function smsAllowlistMatches(e164, allowlist, defaultCountryCode) {
    const target = normalizeMsisdn(e164, defaultCountryCode);
    if (!target)
        return false;
    for (const raw of allowlist) {
        const entry = normalizeMsisdn(String(raw ?? ''), defaultCountryCode);
        if (entry && entry.msisdn === target.msisdn)
            return true;
    }
    return false;
}
//# sourceMappingURL=status-phone.js.map